import moment from 'moment';

import {DataPointColumnsToDataType, DataType, DataTypeToTSType} from 'data-frames/DataFrameTypes';
import {JSONValue} from 'util/Types';

const TYPE_TO_VALIDATE_FN: {[k in DataType]: (value: JSONValue) => value is DataTypeToTSType[k]} = {
  [DataType.BOOLEAN]: (value: JSONValue): value is boolean => typeof value === 'boolean',
  [DataType.DATE_OR_TIMESTAMP]: (value: JSONValue): value is string =>
    typeof value === 'string' && moment(value, moment.ISO_8601, true).isValid(),
  [DataType.JSON]: (value: JSONValue): value is JSONValue => true,
  [DataType.NUMBER]: (value: JSONValue): value is number => typeof value === 'number',
  [DataType.STRING]: (value: JSONValue): value is string => typeof value === 'string',
};

// Type defining the array of DataTypes to check for each key. E.g. x: [DataType.NUMBER]
export type TypesToCheck<DataPoint, OutputColumnTypes extends DataPointColumnsToDataType<DataPoint>> = {
  [column in keyof DataPoint]: Array<OutputColumnTypes[column]>;
};

// Internal data structure to track inferred columns through different functions. columnType = undefined
// means it couldn't be inferred.
interface InferredColumn<DataPoint> {
  columnName: keyof DataPoint & string;
  columnType?: DataType;
}

export interface InvalidOutput<DataPoint extends {[key: string]: unknown}> {
  valid: false;
  invalidColumns: Array<keyof DataPoint & string>;
}

interface ValidOutput<DataPoint, OutputColumnTypes extends DataPointColumnsToDataType<DataPoint>> {
  valid: true;
  columnTypes: OutputColumnTypes;
}

/**
 * Infer the types of columns in a dataset and validate that the inferred types are valid for each column.
 *
 * Iterates over each column and then each of the types in typesToCheck for that column to see if every value
 * in a column matches a certain type. If a column is inconsistently typed or doesn't match any types, we will
 * return an error. To avoid having ambiguous types for a column where multiple types would pass (e.g. a column
 * of dates is also a column of strings), we always check types in the following order and pick the first one
 * that passes as a tie breaker:
 *   1. number
 *   2. dates
 *   3. general string
 *
 * Example usage:
 *
 *    const output = inferAndValidateTypes<
 *      {x: JSONValue; y: JSONValue; label: JSONValue},
 *      {
 *        x: DataType.NUMBER | DataType.STRING | DataType.DATE_OR_TIMESTAMP;
 *        y: DataType.NUMBER;
 *        label: DataType.STRING;
 *      }
 *    >(
 *      [
 *        {x: 1, y: 11, label: "hello"},
 *        {x: 2, y: 22, label: "hello"},
 *        {x: 3, y: 33, label: "bye"},
 *      ],
 *      {
 *        x: [DataType.NUMBER, DataType.DATE_OR_TIMESTAMP, DataType.STRING],
 *        y: [DataType.NUMBER],
 *        label: [DataType.STRING],
 *      }
 *    );
 *
 * The output will be:
 *
 *    {
 *      valid: true,
 *      columnTypes: {
 *          x: DataType.Number,
 *          y: DataType.Number,
 *          label: DataType.STRING,
 *      }
 *    }
 *
 * And the type of columnTypes will be:
 *
 *    {
 *      x: DataType.NUMBER | DataType.STRING | DataType.DATE_OR_TIMESTAMP;
 *      y: DataType.NUMBER;
 *      label: DataType.STRING;
 *     }
 *
 * @template DataPoint The type of a row in the dataset, should be a map where each key is the column name.
 * @template OutputColumnTypes The type of the output inferred types. Maps each column name to a union type of
 *    each DataType that it is compatible with.
 * @param dataset Array of rows that are of type DataPoint
 * @param typesToCheck Map that maps each column name to an array of DataTypes to test against that
 *    column's values.
 * @returns
 * - If input is valid:
      Return {valid: true, columnTypes: *map of column names to inferred types*}
 * - If input is invalid:
 *    Return {valid: false, invalidColumns: *array of column names with errors*}
 *
 * @deprecated This function is implemented with an old version of the DataFrame interface. New uses are discouraged,
 * instead the function should be converted to use the new interface.
 */
// TODO(BNCH-24546): Remove or reimplement data frame utils that use old data frame formats
export default function inferAndValidateTypes<
  DataPoint extends {[key: string]: JSONValue},
  OutputColumnTypes extends DataPointColumnsToDataType<DataPoint>,
>(
  dataset: Array<DataPoint>,
  typesToCheck: TypesToCheck<DataPoint, OutputColumnTypes>
): InvalidOutput<DataPoint> | ValidOutput<DataPoint, OutputColumnTypes> {
  const inferredColumns = inferColumnTypes(dataset, typesToCheck);

  const invalidColumns = inferredColumns.filter((column) => column.columnType === undefined);
  if (invalidColumns.length > 0) {
    return {
      valid: false,
      invalidColumns: invalidColumns.map((invalidColumn) => invalidColumn.columnName),
    };
  }

  return makeValidOutput(inferredColumns);
}

// Define an ordering of types to infer, which is important when types are ambiguous, e.g. a column with only nulls
// or a column with date strings
export const TYPES_TO_INFER_IN_ORDER: Array<DataType> = [
  DataType.BOOLEAN,
  DataType.NUMBER,
  DataType.DATE_OR_TIMESTAMP,
  DataType.STRING,
  DataType.JSON,
];

export function inferColumnTypes<
  DataPoint extends {[key: string]: JSONValue},
  OutputColumnTypes extends DataPointColumnsToDataType<DataPoint>,
>(
  dataset: Array<DataPoint>,
  typesToCheck: TypesToCheck<DataPoint, OutputColumnTypes>
): Array<InferredColumn<DataPoint>> {
  const columnsToInfer = Object.keys(typesToCheck) as Array<keyof DataPoint & string>;

  return columnsToInfer.map((columnName) => {
    const columnValues = dataset.map((dataPoint) => dataPoint[columnName]);

    return {
      columnName,
      columnType: TYPES_TO_INFER_IN_ORDER.filter((columnType) =>
        // @ts-expect-error Not every type in TYPES_TO_INFER_IN_ORDER is necessarily compatible with the compatible
        // types defined by the caller.
        typesToCheck[columnName].includes(columnType)
      ).find((compatibleType) => testValuesType(columnValues, compatibleType)),
    };
  });
}

export function testValuesType(values: Array<JSONValue>, dataType: DataType): boolean {
  return values.every((value) => TYPE_TO_VALIDATE_FN[dataType](value) || value == null);
}

export function makeValidOutput<DataPoint, OutputColumnTypes extends DataPointColumnsToDataType<DataPoint>>(
  inferredColumns: Array<InferredColumn<DataPoint>>
): ValidOutput<DataPoint, OutputColumnTypes> {
  // eslint-disable-next-line benchlint/no-spread-reduce
  const columnTypes = inferredColumns.reduce(
    (accumulator, validatedColumn) => ({
      ...accumulator,
      [validatedColumn.columnName]: validatedColumn.columnType,
    }),
    {}
  ) as OutputColumnTypes;

  return {valid: true, columnTypes};
}
